import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { scrollToElement } from 'src/app/common/app.helpers';
import AppSettings from 'src/app/common/app.settings';
import AppValues from 'src/app/common/app.values';
import {
  createLink,
  getStatus,
} from 'src/app/components/profile/user-profile/view-profile/regular-user-profile/regular-user-profile-helper';
import { CommentPosition, CommentView, LoadedCommentsState, SendMessageData } from 'src/app/interfaces/posts.interface';
import { EntityStatus } from 'src/app/interfaces/views.interface';
import { DialogMessagePlaceholders } from 'src/app/notifications/dialogs/dialog-message/dialog-message-placeholders.model';
import { AccountStatus, ReplyComment, UserBasic } from 'src/app/swagger-api';
import { COMMENT_PLACEHOLDER_PHRASES } from '../comments.values';

@Component({
  selector: 'app-comment',
  templateUrl: './comment.component.html',
  styleUrls: ['./comment.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CommentComponent {
  @Input({ required: true }) public commentPosition: CommentPosition;
  @Input({ required: true }) public commentView: CommentView;
  @Input({ required: true }) public isCommentCreator: boolean;
  @Input({ required: true }) public isAdminSession: boolean;
  @Input({ required: true }) public loadedComments: LoadedCommentsState;

  public readonly DELETED: AccountStatus = AccountStatus.DELETED;

  public readonly AppSettings: typeof AppSettings = AppSettings;
  public readonly COMMENT_QUOTE_LENGTH: number = AppValues.COMMENT_QUOTE_LENGTH;
  public readonly MAX_LENGTH: number = AppValues.COMMENT_MAX_LENGTH;
  public readonly PLACEHOLDER_PHRASES: DialogMessagePlaceholders = COMMENT_PLACEHOLDER_PHRASES;
  public readonly scrollToElement: (elementId: string) => void = scrollToElement;
  public readonly DATE_TIME_FORMAT_LONG: string = AppValues.DATE_TIME_FORMAT_LONG;
  public showReply = false;

  @Output() private removeComment: EventEmitter<CommentPosition> = new EventEmitter<CommentPosition>();
  @Output() private sendMessage: EventEmitter<SendMessageData> = new EventEmitter<SendMessageData>();
  @Output() private dirtyForm: EventEmitter<boolean> = new EventEmitter<boolean>();

  public get indexLabel(): string {
    if (this.isReplyComment) {
      return this.commentPosition.indexRoot.toString() + '-' + this.commentPosition.indexReply.toString();
    } else {
      return this.commentPosition.indexRoot.toString();
    }
  }

  public get repliedCommentInfo(): ReplyComment | null {
    return this.isReplyComment ? (this.commentView.source as ReplyComment) : null;
  }

  public getStatus(user: UserBasic): EntityStatus {
    return getStatus(user);
  }

  public getLink(user: UserBasic): string {
    return createLink(user.account_status, user.user_id);
  }

  public get isReplyComment(): boolean {
    return 'indexReply' in this.commentPosition;
  }

  public get allowedReply(): boolean {
    return !this.isAdminSession && !this.isCommentCreator;
  }

  public onReplyComment(): void {
    this.showReply = !this.showReply;
  }

  public onRemoveComment(): void {
    this.removeComment.emit(this.commentPosition);
  }

  public onSendMessage(value: string): void {
    const comment: SendMessageData = {
      message: value,
      position: this.commentPosition,
    };

    this.sendMessage.emit(comment);
    this.showReply = !this.showReply;
  }

  public onShowReply(): void {
    this.showReply = !this.showReply;
  }

  public setFormStatus(isFormChanged: boolean): void {
    this.dirtyForm.emit(isFormChanged);
  }
}
